
import React from 'react';
import * as ReactRouterDOM from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';

const { NavLink } = ReactRouterDOM as any;

interface ClientSidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const ClientSidebar: React.FC<ClientSidebarProps> = ({ sidebarOpen, setSidebarOpen }) => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();

  const navItems = [
    { name: t('common.dashboard'), path: '/dashboard', icon: HomeIcon },
    { name: t('nav.my_orders'), path: '/dashboard/orders', icon: ClipboardListIcon },
    { name: t('nav.my_codes'), path: '/dashboard/codes', icon: QrCodeIcon },
    { name: t('nav.my_deposits'), path: '/dashboard/deposits', icon: DepositIcon },
    { name: t('nav.services'), path: '/dashboard/services', icon: GridIcon },
    { name: t('nav.my_tickets'), path: '/dashboard/support', icon: SupportIcon },
  ];

  const activeLinkClass =
    'bg-primary-500/12 text-primary-700 dark:bg-primary-500/18 dark:text-primary-300 font-semibold shadow-sm ring-1 ring-primary-500/20 dark:ring-primary-400/25';
  const inactiveLinkClass =
    'text-foreground-muted hover:bg-primary-500/[0.07] dark:hover:bg-white/[0.06] hover:text-foreground';

  return (
    <>
      <div
        className={`fixed inset-0 z-30 bg-slate-900/50 backdrop-blur-[2px] transition-opacity duration-300 lg:hidden ${sidebarOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        aria-hidden="true"
        onClick={() => setSidebarOpen(false)}
      />

      <aside
        className={`fixed inset-y-0 z-40 flex w-64 flex-col border-border bg-surface-card shadow-xl transition-transform duration-300 ease-out dark:shadow-primary-950/20 lg:static lg:translate-x-0 lg:shadow-none rtl:lg:translate-x-0
        border-r rtl:border-r-0 rtl:border-l
        left-0 rtl:left-auto rtl:right-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full rtl:translate-x-full'}`}
      >
        <div
          className="h-1 w-full flex-shrink-0 bg-gradient-brand"
          aria-hidden
        />

        <div className="flex h-full min-h-0 flex-col">
          <div className="flex-shrink-0 border-b border-border bg-gradient-to-br from-primary-500/[0.07] via-surface-card to-accent-500/[0.06] px-4 py-5 dark:from-primary-500/10 dark:via-slate-900 dark:to-accent-500/10">
            <div className="mb-3 flex items-center justify-center">
              <h1 className="text-2xl font-bold tracking-tight">
                <span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">e</span>
                <span className="text-foreground">Services</span>
              </h1>
            </div>
            <div className="rounded-xl border border-primary-500/15 bg-surface/80 px-3 py-2.5 text-center dark:border-white/10 dark:bg-slate-800/60">
              <p className="text-base font-semibold text-foreground">
                {user?.prénom} {user?.nom}
              </p>
              <p className="mt-0.5 truncate text-xs text-foreground-muted">{user?.email}</p>
            </div>
          </div>

          <nav className="admin-scrollbar-thin flex-1 space-y-1 overflow-y-auto px-2 py-4" aria-label={t('common.dashboard')}>
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/dashboard'}
                className={({ isActive }: { isActive: boolean }) =>
                  `flex items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${isActive ? activeLinkClass : inactiveLinkClass}`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="h-5 w-5 shrink-0 opacity-90 ltr:mr-3 rtl:ml-3 rtl:mr-0" />
                {item.name}
              </NavLink>
            ))}
          </nav>

          <div className="mt-auto flex-shrink-0 border-t border-border px-2 py-2">
            <NavLink
              to="/"
              className={({ isActive }: { isActive: boolean }) =>
                `flex w-full items-center rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${isActive ? activeLinkClass : inactiveLinkClass}`
              }
              onClick={() => setSidebarOpen(false)}
            >
              <ExternalLinkIcon className="h-5 w-5 shrink-0 text-accent-600 opacity-90 dark:text-accent-400 ltr:mr-3 rtl:ml-3 rtl:mr-0" />
              {t('nav.site_home')}
            </NavLink>
          </div>

          <div className="flex-shrink-0 border-t border-border p-3">
            <button
              type="button"
              onClick={logout}
              className="flex w-full items-center rounded-xl px-3 py-2.5 text-sm font-medium text-foreground-muted transition-colors hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400"
            >
              <LogoutIcon className="h-5 w-5 shrink-0 ltr:mr-3 rtl:ml-3 rtl:mr-0" />
              {t('common.logout')}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

const Icon: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    {children}
  </svg>
);
const HomeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <Icon className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
  </Icon>
);
const DepositIcon: React.FC<{ className?: string }> = ({ className }) => (
  <Icon className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
  </Icon>
);
const GridIcon: React.FC<{ className?: string }> = ({ className }) => (
  <Icon className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </Icon>
);
const ClipboardListIcon: React.FC<{ className?: string }> = ({ className }) => (
  <Icon className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </Icon>
);
const LogoutIcon: React.FC<{ className?: string }> = ({ className }) => (
  <Icon className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
  </Icon>
);
const ExternalLinkIcon: React.FC<{ className?: string }> = ({ className }) => (
  <Icon className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </Icon>
);
const SupportIcon: React.FC<{ className?: string }> = ({ className }) => (
  <Icon className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.79 4 4s-1.79 4-4 4-4-1.79-4-4c0-1.19.49-2.25 1.25-3.001L5 2z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 22C6.477 22 2 17.523 2 12S6.477 2 12 2s10 4.477 10 10-4.523 10-10 10z" />
  </Icon>
);
const QrCodeIcon: React.FC<{ className?: string }> = ({ className }) => (
  <Icon className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4h2v-4zm-6 0H6.414a1 1 0 00-.707.293L4.293 17.707A1 1 0 004 18.414V20h6v-2.586a1 1 0 00-.293-.707L8 15.414z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
  </Icon>
);

export default ClientSidebar;
